import { useCallback, useMemo } from 'react'
import { FlightsClassTypes } from 'constants/flight'
import { MALDIVES_CHARTER_ROUTE } from 'api/lib/flightDealMap'
import useAllCharterFlightJourneys from 'hooks/CharterFlights/useAllCharterFlightJourneys'
import { useRewardSeatsEnabled } from 'hooks/Flights/useRewardSeatsEnabled'
import { useAppSelector } from 'hooks/reduxHooks'
import getHasLoyaltyAccount from 'loyalty/selectors/getHasLoyaltyAccount'
import { OptimizelyFeatureFlags } from 'constants/optimizely'
import useOptimizelyExperiment from 'hooks/Optimizely/useOptimizelyExperiment'
import { getCharterSelectableCashCabins } from 'lib/maldivesCharter/charterCabins'
import useCharterRewardSeatsIndicatorForDate from './useCharterRewardSeatsIndicatorForDate'
import { buildMaldivesCharterRewardLegs } from '../MaldivesDirectCalendar/maldivesCharterRewardLegs'
import { charterCalendarDayKey } from 'lib/maldivesCharter/charterCalendarDayKey'
import type { CabinDateJourneys, CharterRewardAvailabilityContextValue } from './charterRewardAvailabilityContext'
import type { DateRange } from './types'

interface UseMaldivesCharterCabinJourneysParams {
  occupancies: Array<App.Occupants>
  dateRangesByMonth: Record<string, Array<DateRange>>
  fareClass: FlightsClassTypes
  seatEligiblePassengers: number
}

interface UseMaldivesCharterCabinJourneysResult {
  journeysByCabinDate: Map<FlightsClassTypes, Map<string, CabinDateJourneys>>
  getJourneysForCabinDate: (cabin: FlightsClassTypes, dateKey: string) => CabinDateJourneys | undefined
  rewardIndicator: ReturnType<typeof useCharterRewardSeatsIndicatorForDate>
  charterRewardContextValue: CharterRewardAvailabilityContextValue
}

/**
 * Fetches per-cabin charter flight journeys and builds the reward availability
 * context value. Shared by the desktop and mobile Maldives charter calendars.
 */
export function useMaldivesCharterCabinJourneys({
  occupancies,
  dateRangesByMonth,
  fareClass,
  seatEligiblePassengers,
}: UseMaldivesCharterCabinJourneysParams): UseMaldivesCharterCabinJourneysResult {
  const economyJourneys = useAllCharterFlightJourneys({ charterBundleRoute: MALDIVES_CHARTER_ROUTE, occupancies, fareClass: FlightsClassTypes.ECONOMY })
  const premiumJourneys = useAllCharterFlightJourneys({ charterBundleRoute: MALDIVES_CHARTER_ROUTE, occupancies, fareClass: FlightsClassTypes.PREMIUM })
  const businessJourneys = useAllCharterFlightJourneys({ charterBundleRoute: MALDIVES_CHARTER_ROUTE, occupancies, fareClass: FlightsClassTypes.BUSINESS })
  const isLuxRowEnabled = !!useOptimizelyExperiment(OptimizelyFeatureFlags.luxRow)
  const selectableCashCabins = useMemo(
    () => getCharterSelectableCashCabins(isLuxRowEnabled),
    [isLuxRowEnabled],
  )
  const luxRowJourneys = useAllCharterFlightJourneys({
    charterBundleRoute: MALDIVES_CHARTER_ROUTE,
    occupancies,
    fareClass: FlightsClassTypes.LUXROW,
    disabled: !isLuxRowEnabled,
  })

  const journeysByCabinDate = useMemo(() => {
    const map = new Map<FlightsClassTypes, Map<string, CabinDateJourneys>>()
    const cabinFares: Array<[FlightsClassTypes, ReturnType<typeof useAllCharterFlightJourneys>]> = [
      [FlightsClassTypes.ECONOMY, economyJourneys],
      [FlightsClassTypes.PREMIUM, premiumJourneys],
      ...(isLuxRowEnabled ? [[FlightsClassTypes.LUXROW, luxRowJourneys] as [FlightsClassTypes, ReturnType<typeof useAllCharterFlightJourneys>]] : []),
      [FlightsClassTypes.BUSINESS, businessJourneys],
    ]
    cabinFares.forEach(([cabin, fares]) => {
      const cabinMap = new Map<string, CabinDateJourneys>()
      const departingJourneys = fares?.fares?.[0] ?? []
      const returningJourneys = fares?.fares?.[1] ?? []
      Object.values(dateRangesByMonth).forEach((dateRanges) => {
        dateRanges.forEach(({ checkIn, checkOut }) => {
          const departing = departingJourneys.find((j) =>
            charterCalendarDayKey(j.flightGroup.flights.at(-1)?.arrivalDate) === checkIn,
          )
          const returning = returningJourneys.find((j) =>
            charterCalendarDayKey(j.flightGroup.flights.at(0)?.departingDate) === checkOut,
          )
          cabinMap.set(checkIn, { departing, returning })
        })
      })
      map.set(cabin, cabinMap)
    })
    return map
  }, [economyJourneys, premiumJourneys, luxRowJourneys, businessJourneys, isLuxRowEnabled, dateRangesByMonth])

  const getJourneysForCabinDate = useCallback(
    (cabin: FlightsClassTypes, dateKey: string): CabinDateJourneys | undefined => {
      return journeysByCabinDate.get(cabin)?.get(dateKey)
    },
    [journeysByCabinDate],
  )

  const { rewardLegs, rewardLegsByDate } = useMemo(() => buildMaldivesCharterRewardLegs({
    journeysByCabinDate,
    dateRangesByMonth,
    fareClass,
    cashCabins: selectableCashCabins,
  }), [journeysByCabinDate, dateRangesByMonth, fareClass, selectableCashCabins])

  const isRewardSeatsEnabled = useRewardSeatsEnabled()
  const hasLoyaltyAccount = useAppSelector(getHasLoyaltyAccount)

  const rewardIndicator = useCharterRewardSeatsIndicatorForDate({
    legs: rewardLegs,
    legsByDate: rewardLegsByDate,
    passengerCount: seatEligiblePassengers,
    disabled: !isRewardSeatsEnabled || !hasLoyaltyAccount || rewardLegs.length === 0,
  })

  const charterRewardContextValue = useMemo(() => ({
    ...rewardIndicator,
    getJourneysForCabinDate,
  }), [rewardIndicator, getJourneysForCabinDate])

  return {
    journeysByCabinDate,
    getJourneysForCabinDate,
    rewardIndicator,
    charterRewardContextValue,
  }
}
